import {clone, pick, sortBy, toLower} from 'lodash';
import moment from 'moment-timezone';
import {Fragment, PureComponent} from 'react';
import {withTranslation, WithTranslation} from 'react-i18next';
import {Switch, matchPath} from 'react-router-dom';

import {groupApi} from 'api';
import {userApi} from 'api/user';
import {PageHeader} from 'components';
import {ROUTE_PATH} from 'constants/routes';
import {UserContext} from 'context';
import {history} from 'context/history';
import {Group} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale/translation-keys';
import {UserSummary} from 'types';
import {userAuthUtils} from 'utils';
import {ROUTE_CONFIG} from 'utils/route-config';
import {RedirectToPrivateRoute, PrivateRoute} from 'utils/routes';

import {ActivityFiltersForm} from './ActivityFiltersForm';
import {AgentsStatistic} from './AgentsStatistic';
import {Compliance} from './Compliance';
import {Dashboard} from './Dashboard';
import {PreSessionFlow} from './PreSessionFlow';
import {SessionHistoryPage} from './SessionHistory';
import {ActivityFilterType} from './enums';
import {ActivityFilters} from './types';

const T_KEYS = TRANSLATION_KEYS.USER.ACTIVITY;
const T_KEYS_MENU = TRANSLATION_KEYS.USER.PAGE_NAMES.ACTIVITY;

const URLS = ROUTE_PATH.USER.ACTIVITY;

const initialFilters: ActivityFilters = {
    startDate: null,
    endDate: null,
    eid: null,
    groupId: null,
    subgroupId: null,
    sessionId: '',
    userInfoId: '',
    clientDevice: null,
    result: '',
    resultInformation: '',
    filePipelineStatus: null
};

type State = {
    readonly filters: ActivityFilters;
    readonly pageFilters: Record<string, ActivityFilters>;
    readonly availableFilters: ActivityFilterType[];
    readonly groups: Group[];
    readonly users: UserSummary[];
    readonly isLoading: boolean;
}

type Props = WithTranslation;

class Activity extends PureComponent<Props, State> {

    readonly state: State = {
        filters: clone(this.defaultFilters),
        pageFilters: {},
        availableFilters: [],
        groups: [],
        users: [],
        isLoading: false,
    };

    static readonly contextType = UserContext;
    readonly context: React.ContextType<typeof UserContext>;

    get defaultFilters(): ActivityFilters {
        return {
            ...initialFilters,
            startDate: this.now.add(-14, 'd'),
            endDate: this.now
        };
    }

    get actualFilters(): ActivityFilters | null {
        const currentPath = history.location.pathname;
        return this.state.pageFilters[currentPath] || null;
    }

    get downloadFileDisabled(): boolean {
        return userAuthUtils.isDownloadFileDisabledAuthSource();
    }

    get userPermissions() {
        return userAuthUtils.getUserPermissions().activity;
    }

    get timeZone() {
        return this.context.settings.common.timeZone;
    }

    get now() {
        return moment.tz(this.timeZone);
    }

    get subItems() {
        const {t} = this.props;
        return [
            {
                title: t(T_KEYS_MENU.SESSION_HISTORY),
                link: URLS.SESSION_HISTORY,
            },
            {
                title: t(T_KEYS_MENU.PRE_SESSION_FLOW),
                link: URLS.PRE_SESSION_FLOW,
            },
            {
                title: t(T_KEYS_MENU.DASHBOARD.SELF),
                link: URLS.DASHBOARD,
            },
            {
                title: t(T_KEYS_MENU.AGENT_STATISTIC),
                link: URLS.AGENTS_STATISTIC,
            },
            {
                title: t(T_KEYS_MENU.COMPLIANCE),
                link: URLS.COMPLIANCE,
            },
        ];
    }

    get title() {
        const currentPath = history.location.pathname;
        return this.subItems.find((item) => matchPath(currentPath, {path: item.link}))?.title;
    }

    componentDidMount() {
        this.loadGroups();
        this.loadUsers();
    }

    loadGroups = () => {
        groupApi.getGroups().then((groups) => this.setState({groups}));
    };

    loadUsers = () => {
        userApi.getUsersSummary({activated: true}).then((users) => {
            this.setState({users: sortBy(users, (user) => toLower(user.firstName))});
        });
    };

    handleSubmit = (activeFilters: ActivityFilters) => {
        const {filters, availableFilters} = this.state;
        const currentPath = history.location.pathname;

        const nextFilters = {
            ...filters,
            ...pick(activeFilters, availableFilters)
        };
        const pageFilters = {
            ...initialFilters,
            ...pick(nextFilters, availableFilters)
        };

        this.setState({
            filters: nextFilters,
            pageFilters: {[currentPath]: pageFilters}
        });
    };

    handleToggleLoading = (isLoading: boolean) => {
        this.setState({isLoading});
    };

    onAvailableFiltersChange = (availableFilters: ActivityFilterType[]) => {
        const {filters} = this.state;
        const currentPath = history.location.pathname;

        const pageFilters = {
            ...initialFilters,
            ...pick(filters, availableFilters)
        };

        this.setState({
            availableFilters,
            pageFilters: {[currentPath]: pageFilters}
        });
    };

    renderSessionHistory = () => (
        <SessionHistoryPage
            key='session-history'
            users={this.state.users}
            groups={this.state.groups}
            filters={this.actualFilters}
            setAvailableFilters={this.onAvailableFiltersChange}
            isLoading={this.state.isLoading}
            downloadFileDisabled={this.downloadFileDisabled}
            onToggleLoading={this.handleToggleLoading}
        />
    );

    renderActivityDashboard = () => (
        <Dashboard
            key='dashboard'
            filters={this.actualFilters}
            setAvailableFilters={this.onAvailableFiltersChange}
            isLoading={this.state.isLoading}
            downloadFileDisabled={this.downloadFileDisabled}
            onToggleLoading={this.handleToggleLoading}
        />
    );

    renderAgentsStatistic = () => (
        <AgentsStatistic
            key='agents-statistic'
            agents={this.state.users}
            filters={this.actualFilters}
            setAvailableFilters={this.onAvailableFiltersChange}
            isLoading={this.state.isLoading}
            downloadFileDisabled={this.downloadFileDisabled}
            onToggleLoading={this.handleToggleLoading}
        />
    );

    renderPreSessionFlow = () => (
        <PreSessionFlow
            key='pre-session-flow'
            filters={this.actualFilters}
            setAvailableFilters={this.onAvailableFiltersChange}
            isLoading={this.state.isLoading}
            onToggleLoading={this.handleToggleLoading}
        />
    );

    renderCompliance = () => (
        <Compliance
            key='compliance'
            users={this.state.users}
            filters={this.actualFilters}
            setAvailableFilters={this.onAvailableFiltersChange}
            isLoading={this.state.isLoading}
            onToggleLoading={this.handleToggleLoading}
        />
    );

    render() {
        const {
            title, handleSubmit, renderSessionHistory, renderActivityDashboard, renderAgentsStatistic,
            renderPreSessionFlow, renderCompliance, defaultFilters, actualFilters
        } = this;
        const {t} = this.props;
        const {users, groups, isLoading, availableFilters} = this.state;

        return (
            <Fragment>
                <PageHeader title={title} description={t(T_KEYS.DESCRIPTION)} />
                <ActivityFiltersForm
                    users={users}
                    groups={groups}
                    filters={actualFilters}
                    defaultFilters={defaultFilters}
                    availableFilters={availableFilters}
                    isLoading={isLoading}
                    onSubmit={handleSubmit}
                />
                <Switch>
                    <PrivateRoute {...ROUTE_CONFIG.ACTIVITY.SESSION_HISTORY} component={renderSessionHistory} />
                    <PrivateRoute {...ROUTE_CONFIG.ACTIVITY.PRE_SESSION_FLOW} component={renderPreSessionFlow} />
                    <PrivateRoute {...ROUTE_CONFIG.ACTIVITY.DASHBOARD} component={renderActivityDashboard} />
                    <PrivateRoute {...ROUTE_CONFIG.ACTIVITY.AGENT_STATISTIC} component={renderAgentsStatistic} />
                    <PrivateRoute {...ROUTE_CONFIG.ACTIVITY.COMPLIANCE} component={renderCompliance} />

                    <RedirectToPrivateRoute
                        routes={[
                            ROUTE_CONFIG.ACTIVITY.SESSION_HISTORY,
                            ROUTE_CONFIG.ACTIVITY.PRE_SESSION_FLOW,
                            ROUTE_CONFIG.ACTIVITY.DASHBOARD,
                            ROUTE_CONFIG.ACTIVITY.AGENT_STATISTIC,
                            ROUTE_CONFIG.ACTIVITY.COMPLIANCE
                        ]}
                    />
                </Switch>
            </Fragment>
        );
    }
}

export default withTranslation()(Activity);
